import React, { useEffect, useState } from "react";
import {
  Card, Switch, Tag, Button, Row, Col, Typography,
  Divider, Flex, Spin, message, Select
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { MakeApiCall } from "../../../../apis";
import { Wrapper } from "./style";
import { Link } from "react-router-dom";

const { Title, Text } = Typography;

const AdvancedSettings = ({ userData }) => {
  const [settings, setSettings] = useState({
    gstRecoverable: false,
    tcsRecoverable: false,
    sellerFlex: false,
  });

  const [warehouseIds, setWarehouseIds] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // ── GET warehouse location options ────────────────────────────────────────
  const getWarehouseLocations = async () => {
    setOptionsLoading(true);
    const response = await MakeApiCall(
      "advanced-settings/ledger-warehouse-locations",
      "get", null, true, {}, userData?.token
    );
    if (response?.status) {
      const locations = response?.data?.locations || [];
      setWarehouseOptions(
        locations.map((loc) => ({ label: loc, value: loc }))
      );
    } else {
      message.warning(response?.message);
    }
    setOptionsLoading(false);
  };

  // ── GET advanced-settings ─────────────────────────────────────────────────
  const getAdvancedSettings = async () => {
    setLoading(true);
    const response = await MakeApiCall(
      "advanced-settings", "get", null, true, {}, userData?.token
    );
    if (response?.status) {
      const data = response?.data || {};
      setSettings({
        gstRecoverable: data?.is_tax_input_credit_recoverable ?? false,
        tcsRecoverable: data?.is_tcs_recoverable ?? false,
        sellerFlex: data?.exclude_seller_flex_from_fba ?? false,
      });
      setWarehouseIds(data?.seller_flex_warehouse_id || []);
    } else {
      message.warning(response?.message);
    }
    setLoading(false);
  };

  // ── PUT advanced-settings ─────────────────────────────────────────────────
  const saveAdvancedSettings = async () => {
    if (settings.sellerFlex && warehouseIds.length === 0) {
      message.warning("Please select at least one Seller Flex Location.");
      return;
    }

    const payload = {
      is_tax_input_credit_recoverable: settings.gstRecoverable,
      is_tcs_recoverable: settings.tcsRecoverable,
      exclude_seller_flex_from_fba: settings.sellerFlex,
      seller_flex_warehouse_id: settings.sellerFlex ? warehouseIds : [],
    };

    setSaving(true);
    const response = await MakeApiCall(
      "advanced-settings", "put", payload, true, {}, userData?.token
    );
    if (response?.status) {
      message.success(response?.message || "Settings saved successfully!");
      getAdvancedSettings();
    } else {
      message.warning(response?.message);
    }
    setSaving(false);
  };

  // ── Fetch on mount ────────────────────────────────────────────────────────
  useEffect(() => {
    if (userData?.token) {
      getAdvancedSettings();
      getWarehouseLocations();
    }
  }, [userData?.token]);

  // ── Resize listener ───────────────────────────────────────────────────────
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleToggle = (key) => (checked) => {
    setSettings((prev) => ({ ...prev, [key]: checked }));
  };

  const renderToggleLabel = (value) => (
    <Text style={{ marginLeft: 8, color: value ? "#ff4d4f" : "#999" }}>
      {value ? "Yes" : "No"}
    </Text>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Wrapper style={{ padding: isMobile ? 5 : 24, maxWidth: 900, margin: "0 auto" }}>
      {/* Header */}
      <Title level={3} style={{ marginTop: 8, fontSize: 22 }}>Advanced Settings</Title>
      <Text type="secondary">
        These settings affect how Sellerview calculates your real profit.
        Get them right and your P&amp;L actually means something.
      </Text>

      {/* ── Tax Management ── */}
      <Card style={{ marginTop: 24, borderRadius: 10 }}>
        <div className="d-flex align-items-sm-center mb-5">
          <div className="symbol symbol-50px me-4">
            <span className="symbol-label bg-light-danger">
              <i className="ki-duotone ki-document text-danger fs-1">
                <span className="path1"></span>
                <span className="path2"></span>
                <span className="path3"></span>
              </i>
            </span>
          </div>
          <div className="d-flex align-items-center flex-row-fluid flex-wrap">
            <div className="flex-grow-1 me-2">
              <span className="text-gray-800 fw-bold d-block fs-4">Tax management</span>
              <span className="text-gray-500 fs-6 fw-semibold">Tax input credit & TCS recovery configuration</span>
            </div>
          </div>
        </div>

        <Divider />

        {/* GST */}
        <Row justify="space-between" align="middle">
          <Col>
          <Flex className="mb-2">
            <Text strong>
              Tax input credit is recoverable{" "}
              <Tag className="fw-bold" color="orange">Tax</Tag>
            </Text>
          </Flex>
            <Text type="secondary">
              Enable if you're Tax-registered and claim input tax credit. Sellerview <br />
              will treat Tax paid on Amazon fees and purchases as recoverable <br />
              so it doesn't inflate your cost base.
            </Text>
          </Col>
          <Col>
            <Flex align="center">
              <Switch
                checked={settings.gstRecoverable}
                onChange={handleToggle("gstRecoverable")}
                className={isMobile ? "mt-4" : "mt-0"}
              />
              {renderToggleLabel(settings.gstRecoverable)}
            </Flex>
          </Col>
        </Row>

        {/*Uncomment this when required*/}
        {/*<Divider />*/}

        {/* TCS */}
        {/*<Row justify="space-between" align="middle">*/}
        {/*  <Col>*/}
        {/*  <Flex className="mb-2">*/}

        {/*    <Text strong>*/}
        {/*      TCS is recoverable{" "}*/}
        {/*      <Tag className="fw-bold" color="orange">TCS</Tag>*/}
        {/*    </Text>*/}
        {/*  </Flex>*/}

        {/*    <Text type="secondary">*/}
        {/*      Amazon deducts 1% TCS (0.5% for composition dealers) from every payout. <br />*/}
        {/*      If you file quarterly returns and claim it back, enable this — it won't be treated <br />*/}
        {/*      as a P&L expense.*/}
        {/*    </Text>*/}
        {/*  </Col>*/}
        {/*  <Col>*/}
        {/*    <Flex align="center">*/}
        {/*      <Switch*/}
        {/*        checked={settings.tcsRecoverable}*/}
        {/*        onChange={handleToggle("tcsRecoverable")}*/}
        {/*        className={isMobile ? "mt-4" : "mt-0"}*/}
        {/*      />*/}
        {/*      {renderToggleLabel(settings.tcsRecoverable)}*/}
        {/*    </Flex>*/}
        {/*  </Col>*/}
        {/*</Row>*/}
      </Card>

      {/* ── Inventory Management ── */}
      <Card style={{ marginTop: 20, borderRadius: 10 }}>
        <div className="d-flex align-items-sm-center mb-5">
          <div className="symbol symbol-50px me-4">
            <span className="symbol-label bg-light-danger">
              <i className="ki-duotone ki-cube-2 text-danger fs-1">
                <span className="path1"></span>
                <span className="path2"></span>
                <span className="path3"></span>
              </i>
            </span>
          </div>
          <div className="d-flex align-items-center flex-row-fluid flex-wrap">
            <div className="flex-grow-1 me-2">
              <span className="text-gray-800 fw-bold d-block fs-4">Inventory management</span>
              <span className="text-gray-500 fs-6 fw-semibold">Control how different fulfillment types are counted</span>
            </div>
          </div>
        </div>

        <Divider />

        <Row justify="space-between" align="middle">
          <Col>
          <Flex className="mb-2">

            <Text strong>
              Exclude Seller Flex from FBA inventory{" "}
              <Tag className="fw-bold" color="green">Seller Flex</Tag>
            </Text>
          </Flex>
            <Text type="secondary">
              Seller Flex lets you fulfil from your own warehouse using Amazon's logistics. <br />
              Enable this to exclude Seller Flex stock with FBA inventory for better inventory forecast.
            </Text>
          </Col>
          <Col>
            <Flex align="center">
              <Switch
                checked={settings.sellerFlex}
                onChange={handleToggle("sellerFlex")}
                className={isMobile ? "mt-4" : "mt-0"}
              />
              {renderToggleLabel(settings.sellerFlex)}
            </Flex>
          </Col>
        </Row>

        {/* ── Seller Flex Location Multi-Select ── */}
        {settings.sellerFlex && (
          <div
            style={{
              marginTop: 16,
              padding: 16,
              backgroundColor: "#fafafa",
              borderRadius: 8,
              border: "1px solid #f0f0f0",
            }}
          >
            <Text
              strong
              style={{
                display: "block",
                marginBottom: 10,
                fontSize: 12,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: "#555",
              }}
            >
              Seller Flex Location Code
            </Text>

            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="Select location codes e.g. XIAR, BLR5"
              value={warehouseIds}
              onChange={setWarehouseIds}
              options={warehouseOptions}
              loading={optionsLoading}
              size="large"
              style={{ width: "100%", maxWidth: 480 }}
              maxTagCount="responsive"
              filterOption={(input, option) =>
                option?.value?.toLowerCase().includes(input.toLowerCase())
              }
            />

            <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginTop: 10 }}>
              <InfoCircleOutlined style={{ color: "#8c8c8c", fontSize: 13, marginTop: 2 }} />
              <Text type="secondary" style={{ fontSize: 13 }}>
                Find Seller Flex Code(s): Login to <Link to="https://sellerflex.amazon.in/dashboard" target="_blank" rel="noopener noreferrer">Seller Flex</Link> &gt; Look for Alphanumeric 4 digit code on in top bar.
           
                <br />
                If you have multiple Flex Codes - Click the drop down and paste them all here.
              </Text>
            </div>
          </div>
        )}
      </Card>

      {/* ── Footer ── */}
      {isMobile ? (
        <div style={{ marginTop: 20, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Text type="secondary" style={{ marginBottom: 12, display: "block", width: "100%" }}>
            Changes apply to future P&amp;L calculations only
          </Text>
          <div>
            <Button size="large" onClick={getAdvancedSettings} style={{ marginRight: 10 }}>Discard</Button>
            <Button size="large" type="primary" danger loading={saving} onClick={saveAdvancedSettings}>
              Save settings
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Text type="secondary">Changes apply to future P&amp;L calculations only</Text>
          <div>
            <Button size="large" onClick={getAdvancedSettings} style={{ marginRight: 10 }}>Discard</Button>
            <Button size="large" type="primary" danger loading={saving} onClick={saveAdvancedSettings}>
              Save settings
            </Button>
          </div>
        </div>
      )}
    </Wrapper>
  );
};

export default AdvancedSettings;